import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAnalytics } from "../hooks/useAnalytics";
import useLandingPageIds from "../hooks/useLandingPageIds";
import {
  mergePartnerConfig,
  resolvePartnerSlug,
  usePartnerConfig,
} from "../hooks/usePartnerConfig";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { isMultiStudyConfig, LandingPageConfig } from "../types/landingPage";
import { extractAndSaveAbTest } from "../utils/abTest";
import { resolveAttribution, saveAttributionData } from "../utils/attribution";
import { identify } from "../utils/rudderstack";
import { LandingPageLayout } from "./LandingPageLayout";
import { PreScreeningQuestionnaire } from "./PreScreeningQuestionnaire";
import { MultiStudyPreScreener } from "../features/multi-study";
import { useStudyMedicationVariant } from "../utils/abTestingUtils";
import { getLandingPageConfig } from "../data/landingPageConfigs";

interface ExtendedLandingPageProps {
  config: LandingPageConfig;
}

/**
 * Extended landing page template with pre-screening questionnaire
 * Configurable via LandingPageConfig
 * Redirects to contact page when questionnaire is completed
 */
export function ExtendedLandingPage({ config }: ExtendedLandingPageProps) {
  const navigate = useNavigate();

  const [searchParams, _] = useSearchParams();
  const params = useParams<{ partnerSlug?: string; landingId?: string }>();
  const { variantId, masterId } = useLandingPageIds();
  const { track } = useAnalytics();
  const partnerConfig = usePartnerConfig();

  // Get A/B test variant for medication entry if applicable
  const { variant } = useStudyMedicationVariant(config.id);

  // Determine which prescreening questions to use
  let prescreeningQuestions = config.preScreeningQuestions;

  // If user is in AI variant, try to load the variant config
  if (variant === "ai") {
    const variantId = `${config.id}-b`;
    const variantConfig = getLandingPageConfig(variantId);
    if (variantConfig?.preScreeningQuestions) {
      prescreeningQuestions = variantConfig.preScreeningQuestions;
    }
  }

  useEffect(() => {
    const patientId = searchParams.get("pid");

    // Resolve attribution: partner path slug is the durable signal, query params
    // (m, s, cp) enrich it, and stripped params are reconstructed from the slug +
    // landing config so co-branded pages persist consistent attribution.
    const partnerSlug = resolvePartnerSlug(params, searchParams);
    const attributionData = resolveAttribution(searchParams, {
      partnerSlug,
      defaultCampaign: config.defaultCampaign,
    });

    // Save attribution data to local storage
    saveAttributionData(attributionData);

    // CDN-level A/B tests use pXXX URL params that need to persist for the trial session
    const pData = extractAndSaveAbTest(searchParams);

    // If we have custom source parameter, identify the user with UTM parameters
    if (attributionData.source || patientId) {
      identify(patientId ?? undefined, {
        utm_source: attributionData.source,
        utm_medium: attributionData.medium || "affiliates",
        utm_campaign: attributionData.campaign,
        utm_content: attributionData.utmContent,
        utm_term: attributionData.utmTerm,
        // Include other non-PII attribution data
        match_type: attributionData.matchType,
        ad_set_id: attributionData.adSetId,
        ad_id: attributionData.adId,
        audience: attributionData.audience,
      });

      // Also track a page view with attribution data
      track("lp.page_viewed", {
        landing_page_id: variantId,
        variant_id: variantId,
        master_id: masterId,
        utm_source: attributionData.source,
        utm_medium: attributionData.medium || "affiliates",
        utm_campaign: attributionData.campaign,
        has_custom_params: true,
        pKey: pData?.pKey,
        pValue: pData?.pValue,
      });
    }
  }, [
    searchParams,
    masterId,
    variantId,
    params.partnerSlug,
    params.landingId,
    config.defaultCampaign,
  ]);

  useEffect(() => {
    track("funnel.start", {
      landing_page_id: variantId,
    });
  }, []);

  // Handle qualified event - navigate based on config
  const handleQualifiedEvent = () => {
    // If skipContactFormOnQualified is enabled, go to qualified-redirect page
    if (config.skipContactFormOnQualified) {
      navigate(`/${config.id}/qualified-redirect`);
    } else {
      // Default flow: go to contact page
      navigate(`/${config.id}/contact`);
    }
  };

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white">
        {/* Background orbs container - only visible on larger screens */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          {/* Large icon */}
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          {/* Medium icon */}
          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          {/* Small pulsing icon */}
          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        {/* Main content container - reduced top padding on mobile */}
        <div className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-16 pb-12 md:pb-24">
          <div className="text-center mb-4 md:mb-12">
            {/* Title and subtitle */}
            <h1 className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl px-2 sm:px-4">
              <span className="block mb-3 md:mb-4">{config.heroTitle}</span>
              {config.heroSubtitle && (
                <span className="block text-[1.375rem] md:text-[2rem] lg:text-[2.75rem] bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] md:leading-[1.3] overflow-visible">
                  {config.heroSubtitle}
                </span>
              )}
            </h1>

            {/* Intro paragraph if provided - more compact on mobile */}
            {config.introText && (
              <div className="mt-6 md:mt-8 max-w-md mx-auto">
                <div className="inline-flex items-center justify-center text-secondary-600 bg-secondary-50/80 px-4 py-2 rounded-full border border-secondary-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="h-4 w-4 text-secondary-500 mr-2 flex-shrink-0"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className={config.introTextClassName ?? "text-sm"}>{config.introText}</span>
                </div>
              </div>
            )}

            {/* Longer description - now visible on mobile */}
            {config.longDescription && (
              <div className="mt-6 md:mt-8 max-w-3xl mx-auto px-2">
                {config.longDescription.split("\n").map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-left text-sm md:text-base text-gray-700 leading-relaxed mb-3 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Enhanced desktop layout with side content for larger screens */}
          <div className="grid grid-cols-1 gap-4 md:gap-8">
            {isMultiStudyConfig(config) ? (
              // Multi-study flow
              <div className="w-full max-w-4xl mx-auto">
                <div
                  className="bg-primary-50/50 rounded-2xl overflow-hidden p-4 sm:p-6
                  [&_label]:bg-white
                  [&_label]:border-2
                  [&_label]:border-primary-100
                  [&_label]:rounded-xl
                  [&_label]:p-4
                  [&_label]:mb-3
                  [&_label]:block
                  [&_label]:cursor-pointer
                  [&_label]:transition-all
                  [&_label]:duration-200
                  hover:[&_label]:border-primary-300
                  hover:[&_label]:bg-primary-50
                  hover:[&_label]:shadow-md
                  active:[&_label]:bg-primary-100/70
                  [&_label]:relative
                  [&_label]:pl-12
                  [&_label:hover_span]:text-primary-700
                  [&_label_span]:text-gray-700
                  [&_label_span]:font-medium
                  [&_label_span]:transition-colors
                  [&_label]:before:content-['']
                  [&_label]:before:absolute
                  [&_label]:before:left-4
                  [&_label]:before:top-1/2
                  [&_label]:before:-translate-y-1/2
                  [&_label]:before:h-5
                  [&_label]:before:w-5
                  [&_label]:before:border-2
                  [&_label]:before:border-primary-200
                  [&_label]:before:rounded-full
                  [&_label]:before:transition-colors
                  hover:[&_label]:before:border-primary-400
                  [&_label]:after:content-['']
                  [&_label]:after:absolute
                  [&_label]:after:left-[22px]
                  [&_label]:after:top-1/2
                  [&_label]:after:-translate-y-1/2
                  [&_label]:after:h-2.5
                  [&_label]:after:w-2.5
                  [&_label]:after:rounded-full
                  [&_label]:after:bg-primary-500
                  [&_label]:after:opacity-0
                  [&_label]:after:scale-0
                  [&_label]:after:transition-all
                  hover:[&_label]:after:opacity-100
                  hover:[&_label]:after:scale-100"
                >
                  <MultiStudyPreScreener
                    landingPageId={config.id}
                    commonQuestions={config.commonPreScreeningQuestions}
                    studies={config.studies.map((studyConfig) => ({
                      id: studyConfig.studyId,
                      name: studyConfig.studyName,
                      trialId: studyConfig.trialIdToAutoApply,
                      priority: studyConfig.priority,
                      eligibleZipCodes: Array.from(
                        studyConfig.eligibleZipCodes,
                      ),
                      questions: studyConfig.additionalQuestions || [],
                      commonAnswerRequirements:
                        studyConfig.commonAnswerRequirements,
                      metadata: {},
                    }))}
                    onComplete={handleQualifiedEvent}
                  />
                </div>
              </div>
            ) : prescreeningQuestions ? (
              // Single-study flow (existing behavior)
              <div className="w-full max-w-4xl mx-auto">
                <div
                  className="bg-primary-50/50 rounded-2xl overflow-hidden p-4 sm:p-6
                  [&_label]:bg-white
                  [&_label]:border-2
                  [&_label]:border-primary-100
                  [&_label]:rounded-xl
                  [&_label]:p-4
                  [&_label]:mb-3
                  [&_label]:block
                  [&_label]:cursor-pointer
                  [&_label]:transition-all
                  [&_label]:duration-200
                  hover:[&_label]:border-primary-300
                  hover:[&_label]:bg-primary-50
                  hover:[&_label]:shadow-md
                  active:[&_label]:bg-primary-100/70
                  [&_label]:relative
                  [&_label]:pl-12
                  [&_label:hover_span]:text-primary-700
                  [&_label_span]:text-gray-700
                  [&_label_span]:font-medium
                  [&_label_span]:transition-colors
                  [&_label]:before:content-['']
                  [&_label]:before:absolute
                  [&_label]:before:left-4
                  [&_label]:before:top-1/2
                  [&_label]:before:-translate-y-1/2
                  [&_label]:before:h-5
                  [&_label]:before:w-5
                  [&_label]:before:border-2
                  [&_label]:before:border-primary-200
                  [&_label]:before:rounded-full
                  [&_label]:before:transition-colors
                  hover:[&_label]:before:border-primary-400
                  [&_label]:after:content-['']
                  [&_label]:after:absolute
                  [&_label]:after:left-[22px]
                  [&_label]:after:top-1/2
                  [&_label]:after:-translate-y-1/2
                  [&_label]:after:h-2.5
                  [&_label]:after:w-2.5
                  [&_label]:after:rounded-full
                  [&_label]:after:bg-primary-500
                  [&_label]:after:opacity-0
                  [&_label]:after:scale-0
                  [&_label]:after:transition-all
                  hover:[&_label]:after:opacity-100
                  hover:[&_label]:after:scale-100"
                >
                  <PreScreeningQuestionnaire
                    questions={prescreeningQuestions}
                    onNextScreen={handleQualifiedEvent}
                    title={config.prescreeningTitle ?? "See if you qualify"}
                    subtitle=""
                    landingPageId={config.id}
                    hideProgressBarOnFirstQuestion={config.hideProgressBarOnFirstQuestion}
                    hideProgressBarQuestionCount={config.hideProgressBarQuestionCount}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
